import { Link } from "react-router";
import { useTranslate } from "ra-core";
import { useState } from "react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

import {
  llamadasPor,
  perdidasPorMomento,
  prevision,
  progresoObjetivo,
  textoDias,
  textoMinutos,
  type DatosAnalisis,
  type FilaConversion,
} from "../dominio/analitica";
import { claveOrigen, ORIGENES } from "../dominio/catalogos";
import { claveEtapa } from "../dominio/etapas";
import type { CifrasPanel } from "../dominio/panelInicio";
import type { EtapaPrevision } from "../dominio/tipos";
import { formatoImporte } from "../negocios/formato";
import { Dato, Pct, Td, Th } from "./piezas";

/**
 * Bloques del panel que salen de nevora_panel_analisis (47, Robert, 31/10/2026): dónde se pierde
 * y por qué, qué canal y qué sector funcionan, velocidad y esfuerzo, la mejor hora para llamar,
 * objetivos del mes y previsión. Sin la 47 en la base, el panel enseña los bloques de la 45.
 */

const numero = (n: number) => n.toLocaleString("es-ES");
const conDecimal = (n: number | null) =>
  n == null ? "—" : n.toLocaleString("es-ES", { maximumFractionDigits: 1 });

/** Menos de esto, el % engaña: la fila sale en gris. */
const POCOS = 20;

// ── Fuente de los datos ───────────────────────────────────────────────────────────────
const TODAS = "todas";

export const FiltroFuente = ({
  valor,
  cambiar,
}: {
  valor: string | null;
  cambiar: (v: string | null) => void;
}) => {
  const translate = useTranslate();
  return (
    <Select
      value={valor ?? TODAS}
      onValueChange={(v) => cambiar(v === TODAS ? null : v)}
    >
      <SelectTrigger
        className="h-9 w-auto min-w-[10.5rem] gap-2"
        aria-label={translate("nevora.inicio.fuente.titulo")}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={TODAS}>
          {translate("nevora.inicio.fuente.todas")}
        </SelectItem>
        {ORIGENES.map((o) => (
          <SelectItem key={o} value={o}>
            {translate(claveOrigen(o))}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

// ── Dónde se pierde y por qué ─────────────────────────────────────────────────────────
export const Perdidas = ({ datos }: { datos: DatosAnalisis }) => {
  const translate = useTranslate();
  const t = (k: string, o?: object) =>
    translate(`nevora.inicio.perdidas.${k}`, o);
  const grupos = perdidasPorMomento(datos.perdidas);
  const largo = datos.largo_plazo.reduce((s, f) => s + f.n, 0);
  const motivosLargo = Object.entries(
    datos.largo_plazo.reduce<Record<string, number>>((acc, f) => {
      acc[f.motivo] = (acc[f.motivo] ?? 0) + f.n;
      return acc;
    }, {}),
  )
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);
  if (!grupos.some((g) => g.total) && !largo)
    return <p className="text-sm text-muted-foreground">{t("vacio")}</p>;
  return (
    <div className="space-y-5">
      <div className="grid gap-6 md:grid-cols-3">
        {grupos.map((g) => (
          <div
            key={g.momento}
            className="space-y-3"
            data-testid={`perdidas-${g.momento}`}
          >
            <div className="flex items-baseline justify-between gap-3 border-b border-border/60 pb-2">
              <p className="etiqueta">{t(`momentos.${g.momento}`)}</p>
              <p className="cifra-grande text-xl leading-none">
                {numero(g.total)}
              </p>
            </div>
            {g.motivos.length ? (
              <ul className="space-y-2.5">
                {g.motivos.slice(0, 6).map((m) => (
                  <li key={m.motivo} className="space-y-1">
                    <div className="flex items-baseline justify-between gap-3 text-sm">
                      <span className="truncate" title={m.motivo}>
                        {m.motivo}
                      </span>
                      <span className="shrink-0 tabular-nums text-muted-foreground">
                        {m.n} · {Math.round((m.n / g.total) * 100)} %
                      </span>
                    </div>
                    <span className="block h-1.5 overflow-hidden rounded-full bg-foreground/[0.05]">
                      <span
                        className="block h-full rounded-full bg-foreground/45"
                        style={{ width: `${(m.n / g.total) * 100}%` }}
                      />
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">{t("ninguna")}</p>
            )}
          </div>
        ))}
      </div>
      {largo ? (
        <p className="text-sm text-muted-foreground">
          {t("largo_plazo", { n: largo })}
          {motivosLargo.length
            ? ` ${motivosLargo.map(([m, n]) => `${m} (${n})`).join(" · ")}`
            : ""}
        </p>
      ) : null}
    </div>
  );
};

// ── Qué canal funciona / por sector ───────────────────────────────────────────────────
export const TablaConversion = ({
  filas,
  columna,
  vacio,
  testid,
}: {
  filas: (FilaConversion & { clave: string; etiqueta: string })[];
  columna: string;
  vacio: string;
  testid?: string;
}) => {
  const translate = useTranslate();
  const t = (k: string) => translate(`nevora.inicio.origenes.${k}`);
  if (!filas.length)
    return <p className="text-sm text-muted-foreground">{vacio}</p>;
  return (
    <div className="overflow-x-auto" data-testid={testid}>
      <table className="w-full text-left">
        <thead>
          <tr>
            <Th>{columna}</Th>
            <Th derecha>{t("entrados")}</Th>
            <Th derecha>{t("contactados")}</Th>
            <Th derecha>{t("cita")}</Th>
            <Th derecha>{t("cliente")}</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/60">
          {filas.map((f) => (
            <tr
              key={f.clave}
              className={cn(f.entrados < POCOS && "text-muted-foreground")}
            >
              <Td>{f.etiqueta}</Td>
              <Td derecha>{numero(f.entrados)}</Td>
              <Td derecha>
                <Pct a={f.contactados} b={f.entrados} />
              </Td>
              <Td derecha>
                <Pct a={f.citas} b={f.entrados} />
              </Td>
              <Td derecha>
                <Pct a={f.clientes} b={f.entrados} />
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const Canales = ({ datos }: { datos: DatosAnalisis }) => {
  const translate = useTranslate();
  return (
    <TablaConversion
      testid="tabla-canales"
      columna={translate("nevora.inicio.canales.canal")}
      vacio={translate("nevora.inicio.canales.vacio")}
      filas={datos.canales.map((c) => ({
        ...c,
        clave: c.canal,
        etiqueta: translate(`nevora.inicio.canales.tipos.${c.canal}`),
      }))}
    />
  );
};

export const Sectores = ({ datos }: { datos: DatosAnalisis }) => {
  const translate = useTranslate();
  return (
    <TablaConversion
      testid="tabla-sectores"
      columna={translate("nevora.inicio.sectores.sector")}
      vacio={translate("nevora.inicio.sectores.vacio")}
      filas={datos.sectores.slice(0, 10).map((s) => ({
        ...s,
        clave: s.sector,
        etiqueta: s.sector,
      }))}
    />
  );
};

// ── Velocidad y esfuerzo ──────────────────────────────────────────────────────────────
export const Velocidad = ({
  datos,
  esAdmin,
}: {
  datos: DatosAnalisis;
  esAdmin: boolean;
}) => {
  const translate = useTranslate();
  const t = (k: string, o?: object) =>
    translate(`nevora.inicio.velocidad.${k}`, o);
  const primero = (canales: string[]) =>
    datos.primer_contacto.find((p) => canales.includes(p.canal) && p.n > 0) ??
    null;
  const web = primero(["web_llamada", "web_formulario"]);
  const saliente = primero(["llamada"]);
  const e = datos.esfuerzo;
  const pasos: [string, keyof DatosAnalisis["dias"]][] = [
    [t("pasos.cita"), "cita"],
    [t("pasos.realizada"), "realizada"],
    [t("pasos.propuesta"), "propuesta"],
    [t("pasos.cliente"), "cliente"],
  ];
  const conN = (n: number | undefined) =>
    n ? t("de_leads", { n }) : t("sin_datos");
  return (
    <div className="space-y-6" data-testid="velocidad">
      <dl className="grid grid-cols-2 gap-x-6 gap-y-5 md:grid-cols-3">
        <Dato
          etiqueta={t("primer_web")}
          valor={textoMinutos(web?.mediana_min)}
          detalle={conN(web?.n)}
        />
        <Dato
          etiqueta={t("primer_saliente")}
          valor={textoMinutos(saliente?.mediana_min)}
          detalle={conN(saliente?.n)}
        />
        <Dato
          etiqueta={t("por_contacto")}
          valor={conDecimal(llamadasPor(e.llamadas, e.contestadas))}
          detalle={t("llamadas_total", { n: numero(e.llamadas) })}
        />
        <Dato
          etiqueta={t("por_cita")}
          valor={conDecimal(llamadasPor(e.llamadas, e.citas))}
          detalle={t("citas_total", { n: numero(e.citas) })}
        />
        <Dato
          etiqueta={t("por_cliente")}
          valor={conDecimal(llamadasPor(e.llamadas, e.clientes))}
          detalle={t("clientes_total", { n: numero(e.clientes) })}
        />
        {esAdmin && datos.venta_media ? (
          <Dato
            etiqueta={t("venta_media")}
            valor={
              datos.venta_media.media == null
                ? "—"
                : formatoImporte(datos.venta_media.media)
            }
            detalle={conN(datos.venta_media.n)}
          />
        ) : null}
      </dl>
      <div className="space-y-2">
        <p className="etiqueta">{t("ritmo")}</p>
        <ol className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {pasos.map(([etiqueta, clave]) => (
            <li
              key={clave}
              className="rounded-lg bg-foreground/[0.035] px-3 py-2.5"
            >
              <p className="text-xs text-muted-foreground">{etiqueta}</p>
              <p className="text-base font-medium tabular-nums">
                {textoDias(datos.dias[clave].mediana)}
              </p>
            </li>
          ))}
        </ol>
        <p className="text-xs text-muted-foreground">
          {t("ciclo", {
            dias: textoDias(datos.dias.ciclo.mediana),
            n: datos.dias.ciclo.n,
          })}
        </p>
      </div>
    </div>
  );
};

// ── Mejor hora para llamar ────────────────────────────────────────────────────────────
const DIAS = [1, 2, 3, 4, 5, 6, 7];
/** Por debajo de esto, la celda va en gris: con tan pocas llamadas el % no dice nada. */
const MIN_LLAMADAS_CELDA = 3;

export const MejorHora = ({ datos }: { datos: DatosAnalisis }) => {
  const translate = useTranslate();
  const t = (k: string, o?: object) =>
    translate(`nevora.inicio.franjas.${k}`, o);
  const [tabla, setTabla] = useState(false);
  const franjas = datos.franjas.filter((f) => f.llamadas > 0);
  if (!franjas.length)
    return <p className="text-sm text-muted-foreground">{t("vacio")}</p>;
  const horasConDatos = franjas.map((f) => f.hora);
  const desde = Math.min(9, ...horasConDatos);
  const hasta = Math.max(20, ...horasConDatos);
  const horas = Array.from({ length: hasta - desde + 1 }, (_, i) => desde + i);
  const dias = DIAS.filter((d) => d <= 5 || franjas.some((f) => f.dow === d));
  const celda = (dow: number, hora: number) =>
    franjas.find((f) => f.dow === dow && f.hora === hora);
  const nombreDia = (d: number) => t(`dias.${d}`);
  const mejor = [...franjas]
    .filter((f) => f.llamadas >= MIN_LLAMADAS_CELDA)
    .sort(
      (a, b) =>
        b.contestadas / b.llamadas - a.contestadas / a.llamadas ||
        b.llamadas - a.llamadas,
    )[0];

  if (tabla)
    return (
      <div className="space-y-3">
        <div className="max-h-72 overflow-auto">
          <table className="w-full text-left">
            <thead>
              <tr>
                <Th>{t("dia")}</Th>
                <Th>{t("hora")}</Th>
                <Th derecha>{t("llamadas")}</Th>
                <Th derecha>{t("contestan")}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {franjas
                .sort((a, b) => a.dow - b.dow || a.hora - b.hora)
                .map((f) => (
                  <tr key={`${f.dow}-${f.hora}`}>
                    <Td>{nombreDia(f.dow)}</Td>
                    <Td>{`${f.hora}:00`}</Td>
                    <Td derecha>{numero(f.llamadas)}</Td>
                    <Td derecha>
                      {Math.round((f.contestadas / f.llamadas) * 100)} %
                    </Td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <button
          type="button"
          className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
          onClick={() => setTabla(false)}
        >
          {t("ver_mapa")}
        </button>
      </div>
    );

  return (
    <div className="space-y-3" data-testid="mejor-hora">
      {mejor ? (
        <p className="text-sm">
          {t("mejor", {
            dia: nombreDia(mejor.dow),
            hora: `${mejor.hora}:00`,
            pct: Math.round((mejor.contestadas / mejor.llamadas) * 100),
          })}
        </p>
      ) : null}
      <div className="overflow-x-auto">
        <table className="border-separate border-spacing-[3px] text-center">
          <thead>
            <tr>
              <th className="w-10" />
              {horas.map((h) => (
                <th
                  key={h}
                  className="min-w-[2.1rem] text-[10px] font-medium text-muted-foreground"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dias.map((d) => (
              <tr key={d}>
                <th className="pr-1 text-left text-xs font-medium text-muted-foreground">
                  {t(`dias_corto.${d}`)}
                </th>
                {horas.map((h) => {
                  const c = celda(d, h);
                  const pocos = !c || c.llamadas < MIN_LLAMADAS_CELDA;
                  const pct = c ? c.contestadas / c.llamadas : 0;
                  // Una sola tinta, de clara a oscura según cuántos contestan (gris: pocos datos).
                  const fuerza = Math.round(18 + pct * 82);
                  const titulo = c
                    ? t("celda", {
                        dia: nombreDia(d),
                        hora: `${h}:00`,
                        n: c.llamadas,
                        pct: Math.round(pct * 100),
                      })
                    : t("celda_vacia", { dia: nombreDia(d), hora: `${h}:00` });
                  return (
                    <td
                      key={h}
                      title={titulo}
                      aria-label={titulo}
                      className={cn(
                        "h-7 rounded-[4px] text-[10px] tabular-nums",
                        pocos
                          ? "bg-foreground/[0.04] text-muted-foreground/70"
                          : fuerza > 58
                            ? "text-white"
                            : "text-foreground",
                      )}
                      style={
                        pocos
                          ? undefined
                          : {
                              backgroundColor: `color-mix(in oklab, var(--serie-llamadas) ${fuerza}%, transparent)`,
                            }
                      }
                    >
                      {c && !pocos ? Math.round(pct * 100) : ""}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-2">
          <span>{t("menos")}</span>
          <span
            className="h-2 w-24 rounded-full"
            style={{
              background:
                "linear-gradient(to right, color-mix(in oklab, var(--serie-llamadas) 18%, transparent), var(--serie-llamadas))",
            }}
            aria-hidden
          />
          <span>{t("mas")}</span>
          <span className="ml-2 inline-block size-2.5 rounded-[3px] bg-foreground/[0.06]" />
          <span>{t("pocos", { n: MIN_LLAMADAS_CELDA })}</span>
        </span>
        <button
          type="button"
          className="underline underline-offset-2 hover:text-foreground"
          onClick={() => setTabla(true)}
        >
          {t("ver_tabla")}
        </button>
      </div>
    </div>
  );
};

// ── Previsión ─────────────────────────────────────────────────────────────────────────
export const Prevision = ({
  filas,
  probabilidades,
}: {
  filas: NonNullable<DatosAnalisis["prevision"]>;
  probabilidades: Record<EtapaPrevision, number>;
}) => {
  const translate = useTranslate();
  const t = (k: string, o?: object) =>
    translate(`nevora.inicio.prevision.${k}`, o);
  const p = prevision(
    filas.filter((f) => f.n > 0),
    probabilidades,
  );
  if (!p.lineas.length)
    return <p className="text-sm text-muted-foreground">{t("vacio")}</p>;
  return (
    <div className="space-y-4" data-testid="prevision">
      <dl className="grid grid-cols-2 gap-4">
        <Dato etiqueta={t("ponderado")} valor={formatoImporte(p.ponderado)} />
        <Dato etiqueta={t("abierto")} valor={formatoImporte(p.total)} />
      </dl>
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr>
              <Th>{t("etapa")}</Th>
              <Th derecha>{t("proyectos")}</Th>
              <Th derecha>{t("importe")}</Th>
              <Th derecha>{t("probabilidad")}</Th>
              <Th derecha>{t("previsto")}</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            {p.lineas.map((l) => (
              <tr key={l.etapa}>
                <Td>{translate(claveEtapa(l.etapa))}</Td>
                <Td derecha>{numero(l.n)}</Td>
                <Td derecha>{formatoImporte(l.valor)}</Td>
                <Td derecha>{l.pct} %</Td>
                <Td derecha>{formatoImporte(l.ponderado)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        {p.sinImporte ? `${t("sin_importe", { n: p.sinImporte })} ` : ""}
        <Link
          to="/ajustes"
          className="underline underline-offset-2 hover:text-foreground"
        >
          {t("cambiar")}
        </Link>
      </p>
    </div>
  );
};

// ── Objetivos del mes ─────────────────────────────────────────────────────────────────
export type SumaObjetivos = {
  llamadas: number | null;
  citas: number | null;
  clientes: number | null;
  cobrado: number | null;
};

export const ObjetivosMes = ({
  objetivos,
  actual,
  mes,
  esAdmin,
}: {
  objetivos: SumaObjetivos;
  actual: CifrasPanel;
  mes: string;
  esAdmin: boolean;
}) => {
  const translate = useTranslate();
  const t = (k: string, o?: object) =>
    translate(`nevora.inicio.objetivos.${k}`, o);
  const hoy = new Date();
  const filas: [string, number, number | null, (n: number) => string][] = [
    [t("llamadas"), actual.llamadas, objetivos.llamadas, numero],
    [t("citas"), actual.citas, objetivos.citas, numero],
    [t("clientes"), actual.clientes, objetivos.clientes, numero],
    ...(esAdmin && actual.cobrado != null
      ? ([
          [t("cobrado"), actual.cobrado, objetivos.cobrado, formatoImporte],
        ] as [string, number, number | null, (n: number) => string][])
      : []),
  ];
  const conObjetivo = filas.filter(([, , o]) => !!o);
  return (
    <div
      className="grid gap-x-8 gap-y-5 sm:grid-cols-2 xl:grid-cols-4"
      data-testid="objetivos-mes"
    >
      {conObjetivo.map(([etiqueta, valor, objetivo, formato]) => {
        const p = progresoObjetivo(valor, objetivo, hoy, mes);
        if (!p) return null;
        return (
          <div key={etiqueta} className="space-y-2">
            <div className="flex items-baseline justify-between gap-3">
              <p className="etiqueta">{etiqueta}</p>
              <p
                className={cn(
                  "text-xs font-medium tabular-nums",
                  p.aRitmo
                    ? "text-emerald-700 dark:text-emerald-400"
                    : "text-amber-800 dark:text-amber-300",
                )}
              >
                {p.aRitmo ? t("a_ritmo") : t("por_debajo")}
              </p>
            </div>
            <p className="tabular-nums">
              <span className="cifra-grande text-2xl">{formato(valor)}</span>
              <span className="text-sm text-muted-foreground">
                {" "}
                / {formato(objetivo ?? 0)}
              </span>
            </p>
            <div
              className="relative h-2 overflow-hidden rounded-full bg-foreground/[0.06]"
              role="progressbar"
              aria-label={etiqueta}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.min(p.pct, 100)}
            >
              <span
                className={cn(
                  "absolute inset-y-0 left-0 rounded-full",
                  p.aRitmo ? "bg-emerald-600/80" : "bg-[var(--serie-llamadas)]",
                )}
                style={{ width: `${Math.min(p.pct, 100)}%` }}
              />
              {/* Dónde habría que estar hoy para llegar a fin de mes. */}
              <span
                className="absolute inset-y-0 w-0.5 bg-foreground/50"
                style={{ left: `${p.transcurrido}%` }}
                aria-hidden
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {t("detalle", { pct: p.pct, mes: p.transcurrido })}
            </p>
          </div>
        );
      })}
    </div>
  );
};
